import { toPartsDbVehicleSummary } from '@shared/composables/useVehicleManualSearch/helpers';
import { PartsDbVehicleSummary } from '@unified-commerce/gpc-vue-storefront-parts-db';
import {
  getFacets,
  getFacetStats,
  getVehicleRecords,
  useVehicleSearch,
} from '@unified-commerce/gpc-vue-storefront-search';
import kebabCase from 'lodash/kebabCase';

export interface IVehicleFacet {
  title: string;
  value: string;
}

export const getYearsInRange = (minYear?: number, maxYear?: number): number[] => {
  if (typeof minYear !== 'number' || typeof maxYear !== 'number' || maxYear < minYear) {
    return [];
  }

  return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
};

export const findMakeBySlug = (
  makes: IVehicleFacet[],
  makeSlug: string,
): IVehicleFacet | undefined => makes.find((facet) => kebabCase(facet.value) === makeSlug);

/**
 * `idPrefix` namespaces the underlying `useVehicleSearch` keys. Instances that
 * share a key share results, so each independent consumer (bike page, popup,
 * hero) must pass its own prefix.
 */
// eslint-disable-next-line max-lines-per-function
const useBikeVehicleFacets = (idPrefix = 'bike') => {
  const { search: searchMakes, result: makesResult } = useVehicleSearch(
    `${idPrefix}-make-facet-search`,
  );
  const { search: searchSubmodels, result: submodelsResult } = useVehicleSearch(
    `${idPrefix}-submodel-facet-search`,
  );
  const { search: searchYears, result: yearsResult } = useVehicleSearch(
    `${idPrefix}-submodel-year-facet-search`,
  );
  const { search: searchYearVehicles, result: yearVehiclesResult } = useVehicleSearch(
    `${idPrefix}-submodel-year-vehicles-search`,
  );

  const fetchMakes = async (): Promise<IVehicleFacet[]> => {
    await searchMakes({ facets: ['Make'], filters: 'Hidden:false', resultsPerPage: 0 });
    return getFacets(makesResult.value, 'Make');
  };

  const resolveMake = async (makeSlug: string): Promise<IVehicleFacet | undefined> =>
    findMakeBySlug(await fetchMakes(), makeSlug);

  const fetchSubmodels = async (makeTitle: string): Promise<IVehicleFacet[]> => {
    await searchSubmodels({
      facets: ['Submodel'],
      facetFilters: [`Make:${makeTitle}`],
      filters: 'Hidden:false',
      resultsPerPage: 0,
    });
    return getFacets(submodelsResult.value, 'Submodel');
  };

  const getSubmodelYearRange = async (
    makeTitle: string,
    submodelTitle: string,
  ): Promise<number[]> => {
    await searchYears({
      facets: ['YearFrom', 'YearTo'],
      facetFilters: [`Make:${makeTitle}`, `Submodel:${submodelTitle}`],
      filters: 'Hidden:false',
      resultsPerPage: 0,
    });
    const yearFromStats = getFacetStats(yearsResult.value, 'YearFrom');
    const yearToStats = getFacetStats(yearsResult.value, 'YearTo');
    return getYearsInRange(yearFromStats?.min, yearToStats?.max);
  };

  const resolveYearVehicles = async (
    makeTitle: string,
    submodelTitle: string,
    year: number,
  ): Promise<PartsDbVehicleSummary[]> => {
    await searchYearVehicles({
      facetFilters: [`Make:${makeTitle}`, `Submodel:${submodelTitle}`],
      filters: `YearFrom <= ${year} AND YearTo >= ${year} AND Hidden:false`,
      // A single make+submodel+year combo can match a handful of variant
      // records (different engine codes etc.) — fetch enough to cover them
      // all rather than defaulting to a single hit.
      resultsPerPage: 50,
    });
    return getVehicleRecords(yearVehiclesResult.value).map(toPartsDbVehicleSummary);
  };

  return { fetchMakes, resolveMake, fetchSubmodels, getSubmodelYearRange, resolveYearVehicles };
};

export default useBikeVehicleFacets;
